<template>
  <div class="page-content">
    <div class="intro-container">
      <h1 class="intro-title">Ohjeet – Raviremmi</h1>
      
      <div class="intro-section">
        <p class="intro-text">
          Tervetuloa mukaan Raviremmiin! Raviremmi on virtuaalinen managerointipeli, jossa pääset kokoamaan oman ravitiimisi hevosista ja ohjastajasta. Pelin tarkoituksena on tarjota hauskaa ajanvietettä, kilpailua muita pelaajia vastaan sekä mahdollisuutta kisata kavereiden kesken omissa ryhmissä. Pelin idea on samankaltainen kuin esimerkiksi jääkiekon ja jalkapallon suosituissa fantasia- tai managerointipeleissä, mutta nyt areenana toimivat raviradat!
        </p>
        <div class="important-notice">
          <div class="notice-icon">ℹ️</div>
          <p>
            <strong>Tärkeää:</strong> Raviremmi on taitoon ja tietoon perustuva managerointipeli. Se ei ole vedonlyöntiä eikä siihen rinnastettavaa rahapelitoimintaa. Pelissä ei jaeta rahallisia tai materiaalisia palkintoja.
          </p>
        </div>
      </div>

      <div class="intro-section">
        <h2 class="section-title">Pelin Perusidea</h2>
        <div class="content-card">
          <ul class="feature-list">
            <li class="feature-item">
              <div class="feature-header">
                <span class="feature-icon">🏇</span>
                <strong>Valitse Tiimisi ("Remmi"):</strong>
              </div>
              <p>Jokainen pelaaja kokoaa oman tiiminsä, jota kutsutaan pelissä "remmiksi". Remmiin valitaan 5 hevosta ja 1 ohjastaja.</p>
              <ul class="sub-list">
                <li><strong>Hevosvalikoima:</strong> Valittavanasi on Suomen kilpailurekisterissä olevia ravihevosia. Ulkomaan kilpailurekisterissä olevat hevoset (tunnistettavissa nimessä olevasta '*' merkistä) voivat näkyä tuloslistoilla, mutta niitä ei voi valita tiimiin.</li>
                <li><strong>Ohjastajavalikoima:</strong> Ohjastajista valittavissa on noin 50 viime aikoina parhaiten menestynyttä suomalaista tai Suomessa säännöllisesti kilpailevaa ohjastajaa. Listoilla voi silti näkyä myös muiden ohjastajien pisteet.</li>
              </ul>
              <div class="team-name-warning">
                <div class="warning-icon">⚠️</div>
                <div class="warning-content">
                  <strong>Huomio remmin nimestä:</strong> Jos remmin/tiimin nimi on selvästi sääntöjen vastainen, loukkaava tai erittäin sopimaton, ylläpito voi poistaa tiimin. Tällöin mahdollisesti maksettua summaa ei palauteta.
                </div>
              </div>
            </li>
            <li class="feature-item">
              <div class="feature-header">
                <span class="feature-icon">💰</span>
                <strong>Budjetti:</strong>
              </div>
              <p>Pelaajalla on pelijakson alussa käytössään 600 000 pelirahaa hevosten ja ohjastajan hankkimiseen. Pyri pysymään budjetin rajoissa!</p>
            </li>
            <li class="feature-item">
              <div class="feature-header">
                <span class="feature-icon">🏆</span>
                <strong>Pisteiden Kerääminen:</strong>
              </div>
              <p>Valitsemasi hevoset ja ohjastaja keräävät pisteitä todellisten ravilähtöjen suoritusten perusteella. Mitä paremmin valintasi menestyvät raviradoilla, sitä enemmän pisteitä saat.</p>
            </li>
            <li class="feature-item">
              <div class="feature-header">
                <span class="feature-icon">📅</span>
                <strong>Pelijaksot:</strong>
              </div>
              <p>Peliä pelataan jaksoissa, jotka ovat yleensä noin 32 päivän mittaisia. Jokaisen jakson alussa aloitetaan puhtaalta pöydältä pisteiden ja myös hevosten hintojen osalta.</p>
            </li>
            <li class="feature-item">
              <div class="feature-header">
                <span class="feature-icon">🔄</span>
                <strong>Vaihdot:</strong>
              </div>
              <p>Täysipainoisessa pelaamisessa (maksullinen jakso) käytössäsi on 15 vaihtoa per jakso remmiisi. Vaihdoilla voit reagoida hevosten ja ohjastajien kuntovaihteluihin tai strategisiin näkemyksiisi.</p>
              
              <div class="changes-info-box">
                <h4>📋 Tärkeää tietää vaihdoista:</h4>
                <ul class="changes-info-list">
                  <li><strong>Vahvistaminen pakollista:</strong> Kaikki muutokset on vahvistettava ennen kuin ne tulevat voimaan. Pelkkä muutosten tekeminen ei riitä!</li>
                  <li><strong>Yksi muutossarja kerrallaan:</strong> Voit tehdä vain yhden muutossarjan päivässä. Jos haluat tehdä lisää muutoksia, sinun on ensin peruttava aiemmat vahvistetut muutokset.</li>
                  <li><strong>Muutosten peruminen:</strong> Vahvistetut muutokset voi perua "Peru vahvistetut muutokset" -painikkeella sivun alaosassa.</li>
                  <li><strong>Kilpailujen aikana ei muutoksia:</strong> Muutoksia ei voi tehdä kilpailujen ollessa käynnissä.</li>
                </ul>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div class="intro-section">
        <h2 class="section-title">Peliin Osallistuminen ja Jaksomaksu</h2>
        <div class="content-card">
          <p>Raviremmi tarjoaa mahdollisuuden kokeilla peliä maksutta jokaisen jakson alussa:</p>
          <div class="pricing-options">
            <div class="pricing-option">
              <div class="pricing-header">
                <span class="pricing-icon">🆓</span>
                <h3>Ilmainen Kokeilujakso</h3>
              </div>
              <p>Voit koota remmin ja kokeilla peliä ilmaiseksi kunkin pelijakson ensimmäisen kahden viikon ajan. Tänä aikana näet, miten remmisi pärjää ja kerää pisteitä. Ilmaisessa kokeilussa vaihto-oikeutta ei ole.</p>
            </div>
            <div class="pricing-option">
              <div class="pricing-header">
                <span class="pricing-icon">🔓</span>
                <h3>Koko Jakson Pelaaminen (Maksullinen)</h3>
              </div>
              <p>Jos haluat jatkaa pelaamista koko jakson ajan (yleensä noin 32 päivää) ja saada käyttöösi 15 vaihtoa, voit lunastaa pelioikeuden maksamalla pienen jaksokohtaisen osallistumismaksun. Tämä maksu kattaa pelialustan käytön ja ylläpidon sekä mahdollistaa täysipainoisen pelikokemuksen.</p>
            </div>
          </div>
          <div class="important-notice small">
            <div class="notice-icon">ℹ️</div>
            <p>
              <strong>Huom!</strong> Osallistumismaksu ei ole panos vedonlyöntiin, vaan se mahdollistaa pelin täysien ominaisuuksien käytön kyseisen jakson ajaksi.
            </p>
          </div>
        </div>
      </div>

      <div class="intro-section">
        <h2 class="section-title">Kuinka Premium-päivitys toimii?</h2>
        <div class="content-card">
          <p>
            Premium-version tilaaminen on yksinkertaista ja turvallista. Tässä prosessi vaihe vaiheelta:
          </p>
          
          <div class="payment-steps">
            <div class="payment-step">
              <div class="step-header">
                <div class="step-number">1</div>
                <div class="step-icon">💳</div>
                <h3>Premium-tilaus</h3>
              </div>
              <ul class="step-list">
                <li>Klikkaa "Päivitä Premium-versioon" -painiketta</li>
                <li>Näet hinnan: 3,20 €</li>
                <li>Vahvista tilaus klikkaamalla</li>
              </ul>
            </div>

            <div class="payment-step">
              <div class="step-header">
                <div class="step-number">2</div>
                <div class="step-icon">✅</div>
                <h3>Välitön aktivointi</h3>
              </div>
              <ul class="step-list">
                <li>Saat 15 vaihtoa käyttöösi koko jakson ajaksi</li>
                <li>Voit pelata koko jakson ajan (ei rajoitu 2 viikkoon)</li>
                <li>Tilisi muuttuu "Perus" → "Premium"</li>
              </ul>
            </div>

            <div class="payment-step">
              <div class="step-header">
                <div class="step-number">3</div>
                <div class="step-icon">📧</div>
                <h3>Lasku sähköpostiin</h3>
              </div>
              <ul class="step-list">
                <li>PDF-lasku lähetetään automaattisesti</li>
                <li>Maksuaika: 2 vuorokautta</li>
                <li>Maksa pankkisiirrolla käyttäen viitenumeroa</li>
              </ul>
            </div>
          </div>

          <div class="important-notice small">
            <div class="notice-icon">ℹ️</div>
            <div>
              <p><strong>Tärkeää tietää:</strong></p>
              <ul class="notice-list">
                <li>Premium aktivoituu heti - 14 vrk peruuttamisoikeus ei koske digitaalista sisältöä</li>
                <li>Jos et maksa laskua, ei hätää! Tili palaa automaattisesti Perus-versioon</li>
                <li>Emme lähetä muistutuksia tai peri lisäkuluja</li>
                <li>Emme yritä periä rahaa millään tavalla</li>
                <li>Käytämme vain sähköpostiosoitettasi laskutukseen</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="intro-section">
        <h2 class="section-title">Pistelasku</h2>
        <div class="content-card">
          <p class="mb-4">
            Pisteitä kertyy ainoastaan Suomessa juostavista virallisista Toto-ravilähdöistä. Pisteitä ei siis kerry esimerkiksi paikallisraveista, harjoitusraveista, koelähdöistä tai ulkomailla juostuista lähdöistä, vaikka niihin osallistuisi valitsemasi hevonen tai ohjastaja.
          </p>
          
          <div class="scoring-section">
            <h3 class="scoring-title">
              <span class="scoring-icon">👨‍🏫</span>
              Ohjastajan Pisteet (per huomioitava lähtö)
            </h3>
            <p>Ohjastajasi saa pisteitä seuraavasti sijoituksensa perusteella Suomen Toto-lähdöissä:</p>
            <div class="points-table">
              <div class="points-row">
                <span class="position">Sija 1:</span>
                <span class="points">2 pistettä</span>
              </div>
              <div class="points-row">
                <span class="position">Sija 2 tai 3:</span>
                <span class="points">1 piste</span>
              </div>
              <div class="points-row">
                <span class="position">Muut sijat:</span>
                <span class="points">0 pistettä</span>
              </div>
            </div>
          </div>

          <div class="scoring-section">
            <h3 class="scoring-title">
              <span class="scoring-icon">🐎</span>
              Hevosen Pisteet (per huomioitava lähtö)
            </h3>
            <p>Hevosesi pisteet Suomen Toto-lähdöissä muodostuvat kolmesta osasta: sijoituspisteet, johtopisteet ja voittorahapisteet.</p>
            
            <h4 class="points-category">Sijoituspisteet:</h4>
            <div class="points-table">
              <div class="points-row">
                <span class="position">Ei sijoitusta, keskeytys tai hylkäys:</span>
                <span class="points">2 pistettä</span>
              </div>
              <div class="points-row">
                <span class="position">Sija 1:</span>
                <span class="points">8 pistettä</span>
              </div>
              <div class="points-row">
                <span class="position">Sija 2:</span>
                <span class="points">6 pistettä</span>
              </div>
              <div class="points-row">
                <span class="position">Sija 3:</span>
                <span class="points">5 pistettä</span>
              </div>
              <div class="points-row">
                <span class="position">Sija 4:</span>
                <span class="points">4 pistettä</span>
              </div>
              <div class="points-row">
                <span class="position">Muut sijat (sija 5 ja huonommat):</span>
                <span class="points">3 pistettä</span>
              </div>
            </div>

            <h4 class="points-category">Johtopisteet:</h4>
            <div class="points-table">
              <div class="points-row">
                <span class="position">Jos hevonen on johtanut lähtöä millä tahansa virallisella väliaikapisteellä:</span>
                <span class="points">3 pistettä</span>
              </div>
              <div class="points-row">
                <span class="position">Muussa tapauksessa:</span>
                <span class="points">0 pistettä</span>
              </div>
            </div>

            <h4 class="points-category">Voittorahapisteet:</h4>
            <p>Hevonen saa pisteitä ansaitsemansa palkintorahan perusteella seuraavasti:</p>
            <div class="points-table">
              <div class="points-row">
                <span class="position">0 € – 1000 €:</span>
                <span class="points">1 piste jokaista täyttä 100 € kohden, kuitenkin enintään 10 pistettä</span>
              </div>
              <div class="points-row">
                <span class="position">1001 € – 10 000 €:</span>
                <span class="points">1 piste jokaista alkavaa 1000 € kohden (summan 1000 € ylittävältä osalta)</span>
              </div>
              <div class="points-row">
                <span class="position">Yli 10 000 €:</span>
                <span class="points">1 piste jokaista alkavaa 10 000 € kohden (summan 10 000 € ylittävältä osalta)</span>
              </div>
            </div>

            <div class="total-formula">
              <strong>Hevosen Kokonaispisteet (per lähtö) = Sijoituspisteet + Johtopisteet + Voittorahapisteet</strong>
            </div>

            <div class="important-notice small">
              <div class="notice-icon">🏁</div>
              <div>
                <p><strong>Tasapisteet:</strong> Jos kaksi tai useampi pelaaja päätyy jakson lopussa samaan pistemäärään, järjestys ratkaistaan ensin voittojen määrällä ja jos sekin on tasan, jakson aikana kerätyllä rahasummalla. Jos nämäkin ovat tasan, sijoitus jaetaan.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="intro-section">
        <h2 class="section-title">Ryhmät</h2>
        <div class="content-card">
          <p>
            Voit luoda oman ryhmän tai liittyä kavereidesi ryhmään! Ryhmä on käytännössä oma pieni liigasi, jossa voit kilpailla tuttujasi vastaan ja seurata heidän menestystään.
          </p>
          <div class="feature-grid">
            <div class="feature-card">
              <div class="feature-card-icon">📊</div>
              <h3>Ryhmän Sisäinen Järjestys</h3>
              <p>Näet ryhmän sivulta kaikkien ryhmän jäsenten sijoituksen ja pistemäärän.</p>
            </div>
            <div class="feature-card">
              <div class="feature-card-icon">👥</div>
              <h3>Helppo Seuranta</h3>
              <p>Löydät helposti ryhmäsi jäsenten remmit ja voit seurata heidän valintojaan. Ryhmien tarkoitus on lisätä peliin sosiaalista elementtiä ja hauskaa kilpailuhenkeä kaveriporukassa.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="intro-section">
        <h2 class="section-title">Muutosten Tekeminen ja Vahvistaminen</h2>
        <div class="content-card">
          <p>
            Raviremmissä muutosten tekeminen on kaksivaiheinen prosessi, joka varmistaa että muutokset tulevat voimaan vain kun todella haluat.
          </p>
          
          <div class="process-steps">
            <div class="process-step">
              <div class="step-header">
                <div class="step-number">1</div>
                <div class="step-icon">✏️</div>
                <h3>Tee muutokset</h3>
              </div>
              <ul class="step-list">
                <li>Vaihda hevosia tai ohjastajaa haluamallasi tavalla</li>
                <li>Näet muutokset "Muutokset"-osiossa</li>
                <li>Voit tehdä useita muutoksia kerralla</li>
                <li>Muutokset eivät vielä tule voimaan</li>
              </ul>
            </div>

            <div class="process-step">
              <div class="step-header">
                <div class="step-number">2</div>
                <div class="step-icon">✅</div>
                <h3>Vahvista muutokset</h3>
              </div>
              <ul class="step-list">
                <li>Klikkaa "Vahvista muutokset" -painiketta</li>
                <li>Muutokset tulevat nyt voimaan</li>
                <li>Vaihdot vähennetään saldostasi</li>
                <li>Peliraha siirtyy automaattisesti</li>
              </ul>
            </div>
          </div>

          <div class="important-notice">
            <div class="notice-icon">⚠️</div>
            <div>
              <p><strong>Tärkeää muistaa:</strong></p>
              <ul class="notice-list">
                <li>Voit tehdä vain yhden muutossarjan päivässä</li>
                <li>Jos haluat tehdä lisää muutoksia, peru ensin aiemmat muutokset</li>
                <li>"Peru vahvistetut muutokset" -painike löytyy sivun alaosasta</li>
                <li>Muutoksia ei voi tehdä kilpailujen aikana</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="intro-section">
        <h2 class="section-title">Muuta Huomioitavaa</h2>
        <div class="content-card">
          <div class="info-grid">
            <div class="info-item">
              <div class="info-icon">💲</div>
              <h3>Hintojen Määräytyminen</h3>
              <p>Hevosten ja ohjastajien hinnat määritellään jokaisen uuden pelijakson alussa. Hinnat eivät muutu kesken pelijakson.</p>
            </div>
            <div class="info-item">
              <div class="info-icon">🔄</div>
              <h3>Tulosten Päivitys</h3>
              <p>Kilpailutulokset ja pisteet päivitetään peliin lähtökohtaisesti kilpailupäivinä illalla. Huomioithan, että päivityksissä voi olla viivettä riippuen virallisten tulosten saatavuudesta.</p>
            </div>
            <div class="info-item">
              <div class="info-icon">🚫</div>
              <h3>Ei Palkintoja</h3>
              <p>Kuten aiemmin mainittu, Raviremmissä ei jaeta rahallisia tai materiaalisia palkintoja. Peli on suunniteltu puhtaasti hauskanpitoon, taktiseen managerointiin ja ystävällismieliseen kilpailuun muiden ravi-intoilijoiden kanssa.</p>
            </div>
            <div class="info-item">
              <div class="info-icon">🤝</div>
              <h3>Fair Play</h3>
              <p>Pelaa reilusti ja nauti pelistä!</p>
            </div>
          </div>
        </div>
      </div>

      <div class="intro-section">
        <div class="closing-card">
          <p>
            Toivottavasti nämä ohjeet auttavat sinut alkuun Raviremmin parissa! Jos sinulla on kysyttävää, älä epäröi ottaa yhteyttä pelin ylläpitoon.
          </p>
          <p class="closing-text">
            Onnea peliin!
          </p>
        </div>
      </div>

      <div class="intro-section">
        <h2 class="section-title">Usein kysytyt kysymykset</h2>
        <div class="content-card">
          <div class="faq-container">
            <div class="faq-item">
              <button @click="toggleFaq('password')" class="faq-question">
                <span>Kuinka vaihdan salasanani?</span>
                <span class="faq-arrow" :class="{ 'rotated': openFaq === 'password' }">▼</span>
              </button>
              <div v-show="openFaq === 'password'" class="faq-answer">
                <p>Kirjautumissivulla on "Unohditko salasanasi?" -linkki. Klikkaa sitä ja seuraa ohjeita. Saat sähköpostiisi vahvistuskoodin, jonka avulla voit asettaa uuden salasanan.</p>
              </div>
            </div>

            <div class="faq-item">
              <button @click="toggleFaq('group')" class="faq-question">
                <span>Kuinka liityn ryhmään?</span>
                <span class="faq-arrow" :class="{ 'rotated': openFaq === 'group' }">▼</span>
              </button>
              <div v-show="openFaq === 'group'" class="faq-answer">
                <p>Mene "Ryhmä"-sivulle ja valitse "Liity ryhmään". Tarvitset ryhmän nimen ja salasanan, jotka saat ryhmän luojalta.</p>
              </div>
            </div>

            <div class="faq-item">
              <button @click="toggleFaq('changes-available')" class="faq-question">
                <span>Miksi minulla ei ole vaihtoja käytettävissä?</span>
                <span class="faq-arrow" :class="{ 'rotated': openFaq === 'changes-available' }">▼</span>
              </button>
              <div v-show="openFaq === 'changes-available'" class="faq-answer">
                <p>Uudet pelaajat aloittavat 0 vaihdolla. Täysipainoisessa pelaamisessa (maksullinen jakso) saat 15 vaihtoa käyttöösi. Voit lunastaa pelioikeuden maksamalla jaksokohtaisen osallistumismaksun.</p>
              </div>
            </div>

            <div class="faq-item">
              <button @click="toggleFaq('points')" class="faq-question">
                <span>Milloin pisteet päivittyvät?</span>
                <span class="faq-arrow" :class="{ 'rotated': openFaq === 'points' }">▼</span>
              </button>
              <div v-show="openFaq === 'points'" class="faq-answer">
                <p>Pisteet päivitetään kilpailupäivinä ilallla. Päivityksissä voi olla viivettä riippuen virallisten tulosten saatavuudesta.</p>
              </div>
            </div>

            <div class="faq-item">
              <button @click="toggleFaq('racing')" class="faq-question">
                <span>Miksi en voi tehdä muutoksia remmiin?</span>
                <span class="faq-arrow" :class="{ 'rotated': openFaq === 'racing' }">▼</span>
              </button>
              <div v-show="openFaq === 'racing'" class="faq-answer">
                <p>Muutoksia ei voi tehdä kilpailujen aikana. Odota kilpailujen päättymistä ja pistepäivityksen valmistumista.</p>
                <p><strong>Jos et voi tehdä muutoksia vaikka kilpailut eivät ole käynnissä:</strong></p>
                <ul>
                  <li>Tarkista onko sinulla vahvistettuja muutoksia päivälle</li>
                  <li>Jos on, peru ne ensin "Peru vahvistetut muutokset" -painikkeella sivun alaosassa</li>
                  <li>Tämän jälkeen voit tehdä uusia muutoksia</li>
                </ul>
              </div>
            </div>

            <div class="faq-item">
              <button @click="toggleFaq('changes')" class="faq-question">
                <span>Kuinka muutosten vahvistaminen toimii?</span>
                <span class="faq-arrow" :class="{ 'rotated': openFaq === 'changes' }">▼</span>
              </button>
              <div v-show="openFaq === 'changes'" class="faq-answer">
                <p>Muutosten tekeminen tapahtuu kahdessa vaiheessa:</p>
                <ol>
                  <li><strong>Tee muutokset:</strong> Valitse uusia hevosia tai vaihda ohjastajaa</li>
                  <li><strong>Vahvista muutokset:</strong> Klikkaa "Vahvista muutokset" -painiketta</li>
                </ol>
                <p><strong>Tärkeää:</strong> Muutokset eivät tule voimaan ennen vahvistamista! Voit tehdä useita muutoksia ja vahvistaa ne kaikki kerralla.</p>
                <p>Jos haluat tehdä lisää muutoksia samana päivänä, sinun on ensin peruttava aiemmat vahvistetut muutokset.</p>
              </div>
            </div>

            <div class="faq-item">
              <button @click="toggleFaq('ties')" class="faq-question">
                <span>Miten tasapisteet ratkaistaan?</span>
                <span class="faq-arrow" :class="{ 'rotated': openFaq === 'ties' }">▼</span>
              </button>
              <div v-show="openFaq === 'ties'" class="faq-answer">
                <p>Jos kaksi tai useampi pelaaja päätyy jakson lopussa samaan pistemäärään, järjestys ratkaistaan ensin voittojen määrällä ja jos sekin on tasan, jakson aikana kerätyllä rahasummalla. Jos nämäkin ovat tasan, sijoitus jaetaan.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="intro-section">
        <h2 class="section-title">Tarvitsetko apua?</h2>
        <div class="content-card">
          <div class="contact-info">
            <p>Jos et löydä vastausta yllä olevista ohjeista tai usein kysytyistä kysymyksistä:</p>
            <div class="contact-details">
              <div class="contact-item">
                <span class="contact-label">Tekniset ongelmat:</span>
                <a href="mailto:info@raviremmi.com" class="contact-email">info@raviremmi.com</a>
              </div>
            </div>
            <div class="contact-note">
              <p><strong>Huom:</strong> Ennen yhteydenottoa, tarkista että olet kokeillut:</p>
              <ul>
                <li>Salasanan vaihtoa "Unohditko salasanasi?" -linkin kautta</li>
                <li>Selaimen päivitystä tai toisen selaimen kokeilua</li>
                <li>Yllä olevia ohjeita ja usein kysyttyjä kysymyksiä</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';

export default {
  name: 'IntroductionPage',
  setup() {
    const openFaq = ref(null);

    const toggleFaq = (faqId) => {
      if (openFaq.value === faqId) {
        openFaq.value = null;
      } else {
        openFaq.value = faqId;
      }
    };

    return {
      openFaq,
      toggleFaq
    };
  }
}
</script>

<style scoped>
.page-content {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.intro-container {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.intro-title {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #64ffda;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.intro-section {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #e6f1ff;
  position: relative;
  padding-bottom: 0.75rem;
  margin-bottom: 0.5rem;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 4rem;
  height: 3px;
  background-color: #64ffda;
}

.intro-text {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #a8b2d1;
}

.content-card {
  background: #112240;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  border: 1px solid #1e3a5f;
  color: #a8b2d1;
}

.important-notice {
  display: flex;
  gap: 1rem;
  background-color: rgba(100, 255, 218, 0.1);
  border-left: 4px solid #64ffda;
  padding: 1.25rem;
  border-radius: 8px;
  margin: 1rem 0;
}

.important-notice.small {
  padding: 0.75rem 1rem;
  margin-top: 1.5rem;
}

.notice-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.feature-list {
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.feature-item {
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(100, 255, 218, 0.1);
}

.feature-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.feature-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.feature-icon {
  font-size: 1.5rem;
}

.sub-list {
  margin-top: 0.75rem;
  margin-left: 2.5rem;
  list-style-type: disc;
}

.sub-list li {
  margin-bottom: 0.5rem;
}

.team-name-warning {
  display: flex;
  gap: 0.75rem;
  background-color: rgba(255, 184, 108, 0.1);
  border-left: 4px solid #ffb86c;
  padding: 1rem;
  border-radius: 0 6px 6px 0;
  margin-top: 1rem;
  align-items: flex-start;
}

.warning-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.warning-content {
  flex: 1;
  color: #e6f1ff;
  font-size: 0.9rem;
  line-height: 1.4;
}

.warning-content strong {
  color: #ffb86c;
  display: block;
  margin-bottom: 0.25rem;
}

.pricing-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 1.5rem 0;
}

.pricing-option {
  background-color: #1e3a5f;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid rgba(100, 255, 218, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.pricing-option:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px -12px rgba(0, 0, 0, 0.7);
  border-color: rgba(100, 255, 218, 0.3);
}

.pricing-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.pricing-icon {
  font-size: 1.75rem;
}

.pricing-header h3 {
  color: #64ffda;
  font-size: 1.25rem;
  font-weight: 600;
}

.scoring-section {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(100, 255, 218, 0.1);
}

.scoring-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.scoring-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #64ffda;
  font-size: 1.35rem;
  margin-bottom: 1rem;
}

.scoring-icon {
  font-size: 1.5rem;
}

.points-category {
  color: #e6f1ff;
  font-size: 1.1rem;
  margin: 1.25rem 0 0.75rem;
  font-weight: 600;
}

.points-table {
  background-color: #1e3a5f;
  border-radius: 8px;
  overflow: hidden;
  margin: 1rem 0;
}

.points-row {
  display: flex;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(100, 255, 218, 0.1);
}

.points-row:last-child {
  border-bottom: none;
}

.points-row:nth-child(odd) {
  background-color: rgba(100, 255, 218, 0.05);
}

.position {
  flex: 1;
  font-weight: 500;
}

.points {
  color: #64ffda;
  font-weight: 600;
  text-align: right;
  padding-left: 1rem;
}

.total-formula {
  background-color: rgba(100, 255, 218, 0.1);
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  margin-top: 1.5rem;
  color: #e6f1ff;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.feature-card {
  background-color: #1e3a5f;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid rgba(100, 255, 218, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px -12px rgba(0, 0, 0, 0.7);
  border-color: rgba(100, 255, 218, 0.3);
}

.feature-card-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.feature-card h3 {
  color: #64ffda;
  font-size: 1.2rem;
  margin-bottom: 0.75rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
}

.info-item {
  background-color: #1e3a5f;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid rgba(100, 255, 218, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.info-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px -12px rgba(0, 0, 0, 0.7);
  border-color: rgba(100, 255, 218, 0.3);
}

.info-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.info-item h3 {
  color: #64ffda;
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
}

.closing-card {
  background: linear-gradient(135deg, rgba(10, 25, 47, 0.95) 0%, rgba(17, 34, 64, 0.95) 100%);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  border: 1px solid #1e3a5f;
  color: #a8b2d1;
}

.closing-text {
  font-size: 1.5rem;
  font-weight: 700;
  margin-top: 1.5rem;
  color: #64ffda;
}

.mb-4 {
  margin-bottom: 1rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .intro-title {
    font-size: 1.8rem;
  }
  
  .section-title {
    font-size: 1.5rem;
  }
  
  .pricing-options,
  .feature-grid,
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .points-row {
    flex-direction: column;
    padding: 0.75rem;
  }
  
  .position {
    margin-bottom: 0.25rem;
  }
  
  .points {
    text-align: left;
    padding-left: 0;
  }
}

@media (max-width: 480px) {
  .intro-title {
    font-size: 1.5rem;
  }
  
  .content-card {
    padding: 1rem;
  }
  
  .important-notice {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 1rem;
  }
  
  .feature-header {
    flex-direction: column;
    text-align: center;
  }
  
  .pricing-header {
    flex-direction: column;
    text-align: center;
  }
  
  .scoring-title {
    flex-direction: column;
    text-align: center;
  }
}

/* FAQ Styles */
.faq-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.faq-item {
  border: 1px solid rgba(100, 255, 218, 0.1);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.3s ease;
}

.faq-item:hover {
  border-color: rgba(100, 255, 218, 0.3);
}

.faq-question {
  width: 100%;
  background: none;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #e6f1ff;
  font-size: 1rem;
  font-weight: 600;
  transition: background-color 0.3s ease;
}

.faq-question:hover {
  background-color: rgba(100, 255, 218, 0.05);
}

.faq-arrow {
  color: #64ffda;
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  flex-shrink: 0;
  margin-left: 1rem;
}

.faq-arrow.rotated {
  transform: rotate(180deg);
}

.faq-answer {
  padding: 0 1.25rem 1rem 1.25rem;
  color: #a8b2d1;
  line-height: 1.6;
  border-top: 1px solid rgba(100, 255, 218, 0.1);
  background-color: rgba(100, 255, 218, 0.02);
}

.faq-answer p {
  margin: 0.75rem 0 0 0;
}

/* Contact Styles */
.contact-info {
  text-align: center;
}

.contact-details {
  margin: 1.5rem 0;
  padding: 1rem;
  background-color: rgba(100, 255, 218, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(100, 255, 218, 0.1);
}

.contact-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.contact-label {
  font-weight: 600;
  color: #e6f1ff;
  font-size: 1rem;
}

.contact-email {
  color: #64ffda;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  background-color: rgba(100, 255, 218, 0.1);
  border: 1px solid rgba(100, 255, 218, 0.2);
  transition: all 0.3s ease;
}

.contact-email:hover {
  background-color: rgba(100, 255, 218, 0.15);
  border-color: rgba(100, 255, 218, 0.4);
  transform: translateY(-1px);
}

.contact-note {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: rgba(255, 184, 108, 0.05);
  border-left: 4px solid #ffb86c;
  border-radius: 0 8px 8px 0;
  text-align: left;
}

.contact-note p {
  margin-bottom: 0.75rem;
  color: #e6f1ff;
}

.contact-note ul {
  margin: 0;
  padding-left: 1.5rem;
  color: #a8b2d1;
}

.contact-note li {
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .faq-question {
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
  }
  
  .faq-answer {
    padding: 0 1rem 0.75rem 1rem;
  }
  
  .contact-details {
    padding: 0.75rem;
  }
  
  .contact-note {
    padding: 0.75rem;
  }
}

/* Payment Steps Styles */
.payment-steps {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: 1.5rem 0;
}

.payment-step {
  background-color: #1e3a5f;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid rgba(100, 255, 218, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.payment-step:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px -8px rgba(0, 0, 0, 0.3);
  border-color: rgba(100, 255, 218, 0.3);
}

.step-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.step-number {
  background-color: #64ffda;
  color: #0a192f;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.step-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.step-header h3 {
  color: #64ffda;
  font-size: 1.2rem;
  margin: 0;
  font-weight: 600;
}

.step-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.step-list li {
  padding: 0.5rem 0;
  color: #a8b2d1;
  position: relative;
  padding-left: 1.5rem;
}

.step-list li::before {
  content: '•';
  color: #64ffda;
  font-weight: bold;
  position: absolute;
  left: 0;
}

.notice-list {
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
  color: #a8b2d1;
}

.notice-list li {
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .step-header {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
  
  .step-number {
    align-self: center;
  }
  
  .payment-step {
    padding: 1rem;
  }
  
  .step-list li {
    padding-left: 1rem;
  }
  
  .faq-question {
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
  }
  
  .faq-answer {
    padding: 0 1rem 0.75rem 1rem;
  }
  
  .contact-details {
    padding: 0.75rem;
  }
  
  .contact-note {
    padding: 0.75rem;
  }
}

/* Changes Info Box Styles */
.changes-info-box {
  background-color: rgba(100, 255, 218, 0.08);
  border: 1px solid rgba(100, 255, 218, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.changes-info-box h4 {
  color: #64ffda;
  font-size: 1rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.changes-info-list {
  margin: 0;
  padding-left: 1.5rem;
  color: #a8b2d1;
}

.changes-info-list li {
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

.changes-info-list strong {
  color: #e6f1ff;
}

/* Process Steps Styles */
.process-steps {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: 1.5rem 0;
}

.process-step {
  background-color: #1e3a5f;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid rgba(100, 255, 218, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.process-step:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px -8px rgba(0, 0, 0, 0.3);
  border-color: rgba(100, 255, 218, 0.3);
}

.step-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.step-number {
  background-color: #64ffda;
  color: #0a192f;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.step-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.step-header h3 {
  color: #64ffda;
  font-size: 1.2rem;
  margin: 0;
  font-weight: 600;
}

.step-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.step-list li {
  padding: 0.5rem 0;
  color: #a8b2d1;
  position: relative;
  padding-left: 1.5rem;
}

.step-list li::before {
  content: '•';
  color: #64ffda;
  font-weight: bold;
  position: absolute;
  left: 0;
}

.notice-list {
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
  color: #a8b2d1;
}

.notice-list li {
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .step-header {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
  
  .step-number {
    align-self: center;
  }
  
  .process-step {
    padding: 1rem;
  }
  
  .step-list li {
    padding-left: 1rem;
  }
}
</style>
